import { type OperationVariables } from '@apollo/react-common';
import { type DataProxy } from 'apollo-cache';
import { type GraphQLTaggedNode } from 'react-relay';
import { type ConcreteRequest, createOperationDescriptor } from 'relay-runtime';

import { cloneRelayQueryResult } from './cloneRelayQueryResult.ts';
import { isRelayEnabled } from './isRelayEnabled.ts';
import { toDocumentNode } from './toDocumentNode.ts';
import { getRelayOperationName } from './relayOperation.ts';

export type ReadQueryInteropArgs<TVariables extends Record<string, any> = OperationVariables> =
	Pick<DataProxy.Query<TVariables>, 'variables'> & {
		query: GraphQLTaggedNode;
		/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
		__callerPackage__?: string;
	};

export function readQueryInterop<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(params: ReadQueryInteropArgs<TVariables>): TData | null {
	const operationName = getRelayOperationName(params.query);
	return isRelayEnabled(params.__callerPackage__, { operationName, phase: 'cache' })
		? readQueryInteropRelay<TData, TVariables>(params)
		: readQueryInteropApollo<TData, TVariables>(params);
}

function readQueryInteropRelay<TData, TVariables extends Record<string, any> = OperationVariables>(
	params: ReadQueryInteropArgs<TVariables>,
): TData | null {
	const relayEnv = typeof window !== 'undefined' ? window.GLOBAL_RELAY_ENV : undefined;
	if (!relayEnv) {
		throw new Error(
			`[Relay migration] Missing GLOBAL_RELAY_ENV for readQueryInterop; ${params.__callerPackage__}.`,
		);
	}

	const queryNode = params.query as ConcreteRequest;
	if (!queryNode?.fragment || !queryNode?.operation) {
		return null;
	}

	const variables: Record<string, any> = (params.variables as Record<string, any>) ?? {};
	const operation = createOperationDescriptor(queryNode, variables);
	const snapshot = relayEnv.lookup(operation.fragment);

	return snapshot.isMissingData ? null : cloneRelayQueryResult(snapshot.data as TData);
}

function readQueryInteropApollo<TData, TVariables extends Record<string, any> = OperationVariables>(
	params: ReadQueryInteropArgs<TVariables>,
): TData | null {
	return window['GLOBAL_APOLLO_CLIENT']!.readQuery<TData, TVariables>({
		...params,
		query: toDocumentNode(params.query),
	});
}
